import { useApolloClient } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import {
  PreferenceTargetType,
  Workspace,
  useHydrateWorkspaceLazyQuery,
} from '../../api/graphql/generated';
import { useObservableCacheItem } from '../../cache/observable-cache-hooks';
import { useObservableCache } from '../../cache/observable-cache-provider';
import { logger } from '../../util/logger';
import { getPlatform } from '../../util/platform';
import { VERSION } from '../../version';

export interface SetActiveWorkspaceInput {
  tenantId: string;
}

interface WorkspaceHelpers {
  upsertWorkspaces: (workspaces: Workspace[]) => Promise<void>;
  upsertWorkspace: (workspace: Workspace) => Promise<void>;
  setActiveWorkspace: (input: SetActiveWorkspaceInput) => Promise<void>;
}

type IsAuthenticatedLazyResponse = (tenantId?: string) => boolean;

const WORKSPACES_CACHE_KEY = 'workspaces';
const ACTIVE_WORKSPACE_INDEX = 'activeWorkspaceIndex';

export const useWorkspaceList = (): Workspace[] => {
  return useObservableCacheItem<Workspace[]>(WORKSPACES_CACHE_KEY) ?? [];
};

export const useActiveWorkspaceIndex = (): number | undefined => {
  return useObservableCacheItem<number>(ACTIVE_WORKSPACE_INDEX);
};

export const useWorkspaceListLazy = () => {
  const cache = useObservableCache();

  const callback = useCallback(async () => {
    return cache.getItem<Workspace[]>(WORKSPACES_CACHE_KEY) ?? [];
  }, [cache]);

  return callback;
};

export const useGetActiveWorkspaceLazy = () => {
  const cache = useObservableCache();

  const callback = useCallback(async () => {
    const activeIndex = await cache.getItem<number>(ACTIVE_WORKSPACE_INDEX);
    const workspaces = await cache.getItem<Workspace[]>(WORKSPACES_CACHE_KEY);
    return workspaces?.find((_, index) => index === activeIndex);
  }, [cache]);

  return callback;
};

export const useActiveWorkspace = (): Workspace | undefined => {
  const activeIndex = useActiveWorkspaceIndex();
  const workspaces = useWorkspaceList();
  const workspace = useMemo(() => {
    return workspaces.find((_, index) => index === activeIndex);
  }, [activeIndex, workspaces]);

  return workspace;
};

export const useWorkspaceHelpers = (): WorkspaceHelpers => {
  const cache = useObservableCache();

  return useMemo(
    () => ({
      upsertWorkspaces: async (workspaces: Workspace[]) => {
        await cache.setItem(WORKSPACES_CACHE_KEY, workspaces);
        const activeIndex = await cache.getItem<number>(ACTIVE_WORKSPACE_INDEX);

        if (activeIndex === null) {
          await cache.setItem(ACTIVE_WORKSPACE_INDEX, 0);
        }
      },
      upsertWorkspace: async (workspace: Workspace) => {
        await cache.addItemToList(
          WORKSPACES_CACHE_KEY,
          workspace,
          (itemToAdd, entry) => {
            return itemToAdd.tenant.id === entry.tenant.id;
          }
        );

        const activeIndex = await cache.getItem<number>(ACTIVE_WORKSPACE_INDEX);

        if (activeIndex === null) {
          await cache.setItem(ACTIVE_WORKSPACE_INDEX, 0);
        }
      },
      setActiveWorkspace: async (input: SetActiveWorkspaceInput) => {
        const workspaces =
          await cache.getItem<Workspace[]>(WORKSPACES_CACHE_KEY);

        const activeIndex = workspaces?.findIndex(
          (w) => w.tenant.id === input.tenantId
        );

        await cache.setItem(ACTIVE_WORKSPACE_INDEX, activeIndex);
      },
    }),
    [cache]
  );
};

export const useIsAuthenticatedLazy = (): IsAuthenticatedLazyResponse => {
  const activeWorkspace = useActiveWorkspace();
  const workspaces = useWorkspaceList();

  return (tenantId) => {
    if (tenantId) {
      const workspaceIndex = workspaces.findIndex(
        (w) => w.tenant.id === tenantId
      );
      if (workspaceIndex > -1 && workspaces[workspaceIndex].credentials) {
        return true;
      }
    } else {
      if (activeWorkspace?.credentials !== undefined) {
        return true;
      }
    }

    return false;
  };
};

export const useHydrateAppLazy = (): (() => Promise<void>) => {
  const workspace = useActiveWorkspace();

  const [hydrate] = useHydrateWorkspaceLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const callback = useCallback(async () => {
    if (workspace?.user.id) {
      logger.info('hydrating app');
      await hydrate({
        variables: {
          userInput: {
            id: workspace.user.id,
          },
          teamListInput: {
            limit: 25,
            active: true,
          },
          featureInput: {
            appVersion: VERSION,
            platform: getPlatform(),
          },
          userPreferenceListInput: {
            targetId: workspace.user.id,
            targetType: PreferenceTargetType.USER,
          },
          tenantPreferenceListInput: {
            targetId: workspace.tenant.id,
            targetType: PreferenceTargetType.TENANT,
          },
        },
      });
    }
  }, [hydrate, workspace]);

  return callback;
};

export const useIsAuthenticated = (tenantId?: string): boolean => {
  const checkAuth = useIsAuthenticatedLazy();
  return checkAuth(tenantId);
};

type SwitchWorkspaceCallback = (tenantId: string) => Promise<void>;

export const useSwitchWorkspaceLazy = (): SwitchWorkspaceCallback => {
  const { setActiveWorkspace } = useWorkspaceHelpers();
  const workspace = useActiveWorkspace();
  const client = useApolloClient();
  const hydrateApp = useHydrateAppLazy();

  const callback: SwitchWorkspaceCallback = useCallback(
    async (tenantId: string) => {
      if (workspace?.tenant.id !== tenantId) {
        await client.resetStore();
        setActiveWorkspace({ tenantId });
      } else {
        hydrateApp();
      }
    },
    [client, hydrateApp, setActiveWorkspace, workspace?.tenant.id]
  );

  return callback;
};
